import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import Header from "./Header";
import Tooltip from "../../../Components/common/Tooltip/Tooltip";

import {
  RequestGetFileTypeSettings,
  RequestSetFileTypeSettings,
  RequestGetSandboxSettings,
  RequestSetSandboxSettings,
} from "../../../store/actions/mailGatewayActions";

const FileTypeSettingsForm = ({ selectedProduct, selectedHost }) => {
  const dispatch = useDispatch();

  const [settings, setSettings] = useState({
    sandbox_control: "",
  });

  const urlSettings = useSelector((state) => state.mailGateway.sandboxSettings);
  const urlSettingsLoading = useSelector((state) => state.mailGateway.sandboxSettingsLoading);
  const setUrlSettingsLoading = useSelector((state) => state.mailGateway.setSandboxSettingsLoading);

  // Refs to track initial data for change detection
  const initialSandboxSettings = useRef(null);
  const initialFileTypes = useRef(null);

  useEffect(() => {
    dispatch(RequestGetSandboxSettings(selectedHost));
  }, [dispatch, selectedHost]);

  useEffect(() => {
    if (urlSettings && selectedHost) {
      setSettings((prevSettings) => ({
        ...prevSettings,
        ...urlSettings,
      }));
      // Store initial sandbox settings for change detection
      initialSandboxSettings.current = { ...urlSettings };
    }
  }, [urlSettings, selectedHost]);

  const inputChangeHandler = (e) => {
    const { name, value } = e.target;
    setSettings((prevSettings) => ({
      ...prevSettings,
      [name]: value,
    }));
  };

  const fileTypesFromDB = useSelector((state) => state.mailGateway.fileTypeSettings);
  const loading = useSelector((state) => state.mailGateway.fileTypeSettingsLoading);

  const [fileTypes, setFileTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");

  const selectedItem = fileTypes.find((item) => item.type === selectedType);
  const analysisList = fileTypes.filter((ft) => ft.action === "analysis");
  const blockList = fileTypes.filter((ft) => ft.action === "block");
  const allowList = fileTypes.filter((ft) => ft.action === "allow");

  useEffect(() => {
    if (selectedHost) {
      dispatch(RequestGetFileTypeSettings(selectedHost));
    }
  }, [selectedHost, dispatch]);

  useEffect(() => {
    if (fileTypesFromDB && selectedHost) {
      setFileTypes(fileTypesFromDB);
      // Store initial file types for change detection
      initialFileTypes.current = JSON.parse(JSON.stringify(fileTypesFromDB));
    }
  }, [fileTypesFromDB, selectedHost]);

  const moveTo = (newAction) => {
    if (!selectedItem || selectedItem.action === newAction) return;

    setFileTypes((prev) =>
      prev.map((ft) => (ft.type === selectedItem.type ? { ...ft, action: newAction } : ft)),
    );
    setSelectedType("");
  };

  const saveHandler = () => {
    if (urlSettingsLoading || setUrlSettingsLoading) return;

    const { url_settings_status, ...subOptions } = settings;
    dispatch(RequestSetSandboxSettings({ host: selectedHost, reqBody: subOptions }));
  };

  // Check if sandbox settings have changed
  const hasSandboxSettingsChanged = () => {
    if (!initialSandboxSettings.current) return false;
    
    const currentSettings = { ...settings };
    const initialSettings = { ...initialSandboxSettings.current };
    
    // Remove url_settings_status for comparison (as it's excluded in saveHandler)
    delete currentSettings.url_settings_status;
    delete initialSettings.url_settings_status;
    
    return JSON.stringify(currentSettings) !== JSON.stringify(initialSettings);
  };

  // Check if file types have changed
  const hasFileTypesChanged = () => {
    if (!initialFileTypes.current) return false;
    
    return JSON.stringify(fileTypes) !== JSON.stringify(initialFileTypes.current);
  };

  const handleSaveAll = () => {
    let hasChanges = false;

    // Save file type settings only if changed
    if (hasFileTypesChanged()) {
      dispatch(RequestSetFileTypeSettings({ hostname: selectedHost, settings: fileTypes }));
      hasChanges = true;
      // Update initial file types after save
      initialFileTypes.current = JSON.parse(JSON.stringify(fileTypes));
    }

    // Save sandbox settings only if changed
    if (hasSandboxSettingsChanged()) {
      saveHandler();
      hasChanges = true;
      // Update initial sandbox settings after save
      initialSandboxSettings.current = { ...settings };
    }

    if (!hasChanges) {
      console.log("No changes detected. Skipping save operation.");
      // You could also show a toast notification here
    }
  };

  // Check if there are any changes to enable/disable save button
  const hasAnyChanges = () => {
    return hasFileTypesChanged() || hasSandboxSettingsChanged();
  };

  const renderList = (items) => (
    <div>
      {items.map((item) => (
        <div
          className={`products-body-form__file-list__item ${
            selectedType === item.type ? "products-body-form__file-list__item--active" : ""
          }`}
          key={item.type}
          onClick={() => setSelectedType(item.type)}>
          {item.type}
        </div>
      ))}
    </div>
  );

  const getArrow = (from, to) => {
    if (!selectedItem) return "→";
    return selectedItem.action === from ? "→" : "←";
  };

  const isBetween = (a, b) =>
    selectedItem && (selectedItem.action === a || selectedItem.action === b);

  return (
    <>
      <Header selectedProduct={selectedProduct} selectedHost={selectedHost} />
      <form className="products-body-form">
        <div className="products-body-form__line">
          <div className="mailing__content__security m-b-20">
            <p className="light m-b-10 mailing__content__security__header">
              Sandbox Analysis
              <Tooltip text="If enabled, system will do sandbox analyis" showFlowGrow={true} />
            </p>
            <div className="mailing__content__security__labels">
              <label className="align-items">
                <input
                  className="mailing__content__security__input"
                  type="radio"
                  value="1"
                  name="sandbox_control"
                  checked={settings.sandbox_control === "1"}
                  onChange={inputChangeHandler}
                />
                <p className="margin-left-12">Enabled</p>
              </label>
              <label className="align-items">
                <input
                  className="mailing__content__security__input"
                  type="radio"
                  value="0"
                  name="sandbox_control"
                  checked={settings.sandbox_control === "0"}
                  onChange={inputChangeHandler}
                />
                <p className="margin-left-12">Disabled</p>
              </label>
            </div>
          </div>
        </div>
        <div className="products-body-form__line">
          <div className="mailing__content__security ">
            <p className="light m-b-5  mailing__content__security__header">
              File Type Actions
              <Tooltip text="If enabled, system will do below file type actions" showFlowGrow={true} />
            </p>
          </div>
        </div>
        <div className="products-body-form__line" style={{ display: "flex", gap: "20px" }}>
          <div className="products-body-form__file-list">
            <h4>Analysis List</h4>
            {renderList(analysisList)}
          </div>

          <button
            className="primary-button arrow-button"
            disabled={!isBetween("analysis", "block")}
            onClick={() => moveTo(selectedItem?.action === "analysis" ? "block" : "analysis")}>
            {getArrow("analysis", "block")}
          </button>

          <div className="products-body-form__file-list">
            <h4>Block List</h4>
            {renderList(blockList)}
          </div>

          <button
            className="primary-button arrow-button"
            disabled={!isBetween("block", "allow")}
            onClick={() => moveTo(selectedItem?.action === "block" ? "allow" : "block")}>
            {getArrow("block", "allow")}
          </button>

          <div className="products-body-form__file-list">
            <h4>Allow List</h4>
            {renderList(allowList)}
          </div>
        </div>

        <div className="products-body-form__line" style={{ marginTop: "18px" }}>
          <button
            type="button"
            className="save-button primary-button"
            disabled={loading || urlSettingsLoading || setUrlSettingsLoading || !hasAnyChanges()}
            onClick={handleSaveAll}>
            Save Settings
          </button>
        </div>
      </form>
    </>
  );
};

export default FileTypeSettingsForm;